import { useEffect, useState, useCallback } from "react";
import "./App.css";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Check,
  Loader2,
  Pencil,
  Share2,
  Users,
  PartyPopper,
  X,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
console.log("API URL:", API);
const VISITOR_KEY = "engagement_poll_visitor";
const VOTED_KEY = "engagement_poll_voted";

function getVisitorId() {
  let id = localStorage.getItem(VISITOR_KEY);
  if (!id) {
    id =
      (crypto.randomUUID && crypto.randomUUID()) ||
      `v-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(VISITOR_KEY, id);
  }
  return id;
}

function Poll() {
  const [visitorId] = useState(getVisitorId);
  const [data, setData] = useState(null);
  const [selection, setSelection] = useState([]);
  const [hasVoted, setHasVoted] = useState(
    () => localStorage.getItem(VOTED_KEY) === "true"
  );
  const [editing, setEditing] = useState(
    () => localStorage.getItem(VOTED_KEY) !== "true"
  );
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);

  const fetchPoll = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/poll`, {
        params: { visitor_id: visitorId },
      });
      setData(res.data);
      if (res.data.selection?.length) {
        setSelection((cur) => (cur.length ? cur : res.data.selection));
      }
    } catch (e) {
      console.error("Erreur de chargement du sondage", e);
    }
  }, [visitorId]);

  useEffect(() => {
    fetchPoll();
  }, [fetchPoll]);

  useEffect(() => {
    if (editing) return;
    const t = setInterval(fetchPoll, 4000);
    return () => clearInterval(t);
  }, [editing, fetchPoll]);

  const toggle = (id) => {
    if (!editing) return;
    setSelection((cur) =>
      cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]
    );
  };

  const submitVote = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/vote`, {
        visitor_id: visitorId,
        option_ids: selection,
      });
      setData(res.data);
      setSelection(res.data.selection || []);
      localStorage.setItem(VOTED_KEY, "true");
      setHasVoted(true);
      setEditing(false);
      setCelebrate(true);
    } catch (e) {
      console.error("Erreur lors du vote", e);
    } finally {
      setSubmitting(false);
    }
  };

  const share = async () => {
    const url = window.location.href;
    const payload = {
      title: "Une grande chaine de jeûne pour la Vie",
      text: "Votez pour le(s) jour(s) qui vous conviennent (7 → 15 juillet)",
      url,
    };
    try {
      if (navigator.share) {
        await navigator.share(payload);
        return;
      }
    } catch (e) {
      /* utilisateur a annulé — on retombe sur la copie */
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (e) {
      console.error("Impossible de copier le lien", e);
    }
  };

  const participants = data?.participants ?? 0;
  const maxCount = Math.max(1, ...(data?.options || []).map((o) => o.count));
  const totalSelections = (data?.options || []).reduce(
    (sum, o) => sum + o.count,
    0
  );

  return (
    <div className="min-h-screen w-full bg-[#FAF8F5] text-[#171512] flex justify-center px-5 py-12 sm:py-20 selection:bg-[#DDE7DA]">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <div className="flex items-center justify-between gap-4">
            <p
              className="text-xs uppercase tracking-[0.35em] text-[#5A1028]"
              data-testid="poll-eyebrow"
            >
              9 jours pour obtenir un miracle
            </p>
            <button
              type="button"
              onClick={share}
              data-testid="share-button"
              className="flex shrink-0 items-center gap-2 rounded-full border border-[#5A1028] bg-white px-4 py-2 text-xs font-semibold text-[#3C6B4E] transition-colors hover:border-[#5A1028]"
            >
              <Share2 className="h-3.5 w-3.5" />
              {copied ? "Lien copié !" : "Partager"}
            </button>
          </div>
          <h1
            className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
            data-testid="poll-question"
          >
            {"Une grande chaine de jeûne pour la Vie"}
          </h1>

          <div className="mt-4 text-sm text-[#6B675C]">
            <p className="text-base leading-relaxed text-[#2F2F35]">
              Choix des jours de jeûne pour la Vie en bas de page.
            </p>
          </div>

          <div
            className="mt-6 rounded-2xl border border-[#E4E1D8] bg-white p-6"
            data-testid="poll-intro"
          >
            <p className="text-base leading-relaxed text-[#2F2F35]">
              Le 15 juillet prochain, l'Assemblée nationale doit procéder au vote définitif
              sur la proposition de loi visant à <b>créer un droit à l'aide à mourir</b>.

              <br />L'espérance chrétienne nous invite à croire que la prière et le jeûne peuvent changer le cours des événements.
              En 2026, en Écosse, alors qu'un texte comparable semblait devoir être adopté, 
              douze députés ont finalement changé d'avis au moment du vote, modifiant l'issue du scrutin.

              <br />À l'exemple que nous donne la Sainte Écriture dans le livre d'Esther, 
                où le Seigneur accorda une délivrance inespérée à son peuple après un temps de jeûne et de prière, 
                et en communion avec le pape Léon XIV, qui appelle à « défendre la dignité intrinsèque de toute personne humaine »,
                <b> nous invitons les catholiques qui le peuvent à jeûner pendant l'un des neuf jours précédant ce vote, 
                afin de demander au Seigneur la grâce que cette loi ne soit pas adoptée.</b>
            </p>
          </div>

          <div>
            <b>Le jeûne est une arme spirituelle qui renverse les situations les plus extrêmes
              </b>{" "}
              (cf Mt 17, 21).

            <br /><button
              type="button"
              onClick={() => setShowExplanation((v) => !v)}
              className="ml-3 text-sm font-semibold text-[#8B1E3F] hover:text-[#5A1028] underline underline-offset-2"
            >
              {showExplanation ? "Masquer le rappel" : "Rappel sur le jeûne"}
            </button>

            <AnimatePresence>
              {showExplanation && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden mt-4 rounded-lg bg-[#FAF6F7] border border-[#E4E1D8] p-4 text-sm leading-relaxed text-[#3F3B33]"
                >
                  Il consiste avant tout à diminuer la quantité de nourriture ingérée, dans un esprit de sobriété et de pénitence.
                  <br />L'Église recommande ainsi un seul vrai repas (en s'abstenant si possible de viande),
                  avec éventuellement une collation pendant cette journée jeûnée.
                </motion.div>
              )}
            </AnimatePresence>

            <br /><br />Demandons ce miracle en jeûnant et en priant tout particulièrement la Vierge Marie, Mère de Miséricorde :

            <br /><i>Souvenez-vous, ô très miséricordieuse Vierge Marie,
              qu'on n'a jamais entendu dire qu'aucun de ceux qui ont eu recours à votre protection,
              imploré votre assistance et réclamé vos suffrages, ait été abandonné. 
              Animé d'une pareille confiance, ô Vierge des vierges, ô ma Mère,
              je cours vers vous et, gémissant sous le poids de mes péchés, je me prosterne à vos pieds.
              Ô Mère du Verbe, ne méprisez pas mes prières, mais accueillez-les favorablement et daignez les exaucer.
              Amen.</i>
          </div>

          <p className="mt-4 text-sm text-[#6B675C]">
            Vous pouvez sélectionner un ou plusieurs jours de jeûne
            et modifier votre choix à tout moment.
          </p>
          <div className="mt-6 h-px w-full bg-[#E4E1D8]" />
        </motion.div>

        {/* Options */}
        <div className="mt-8 space-y-3" data-testid="poll-options">
          {(data?.options || []).map((opt, i) => {
            const barPct = Math.round((opt.count / maxCount) * 100);
            const isChecked = selection.includes(opt.id);
            const showBar = !editing;

            return (
              <motion.button
                key={opt.id}
                type="button"
                disabled={!editing}
                onClick={() => toggle(opt.id)}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 + i * 0.04 }}
                data-testid={`poll-option-${opt.id}`}
                className={`relative w-full overflow-hidden rounded-xl border text-left transition-colors duration-200 ${
                  editing
                    ? isChecked
                      ? "border-[#3C6B4E] bg-white shadow-sm cursor-pointer"
                      : "border-[#E4E1D8] bg-white hover:border-[#B9B4A5] cursor-pointer"
                    : "cursor-default border-[#E4E1D8] bg-white"
                }`}
              >
                {showBar && (
                  <motion.div
                    className={`absolute inset-y-0 left-0 ${
                      isChecked ? "bg-[#DBE8DC]" : "bg-[#EFEDE5]"
                    }`}
                    initial={{ width: 0 }}
                    animate={{ width: `${barPct}%` }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    data-testid={`poll-bar-${opt.id}`}
                  />
                )}

                <div className="relative flex items-center justify-between gap-4 px-5 py-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <span
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-colors ${
                        isChecked
                          ? "border-[#3C6B4E] bg-[#3C6B4E]"
                          : "border-[#C6C1B4] bg-white"
                      }`}
                    >
                      {isChecked && (
                        <Check className="h-3 w-3 text-white" strokeWidth={3} />
                      )}
                    </span>
                    <span className="truncate text-base sm:text-lg font-medium">
                      {opt.label}
                    </span>
                  </div>

                    <div className="relative flex shrink-0 items-baseline gap-1.5 tabular-nums">
                      <span
                        className="text-lg font-semibold"
                        data-testid={`poll-count-${opt.id}`}
                      >
                        {opt.count}
                      </span>
                      <span className="text-xs text-[#8A8577]">
                        {opt.count > 1 ? "participants" : "participant"} 
                      </span>
                    </div>
                </div>
              </motion.button>
            );
          })}
        </div>

        {/* Actions */}
        <div className="mt-8">
          <div className="mb-4 flex items-center gap-2 text-sm text-[#6B675C]">
            <Users className="h-4 w-4" />
            <span>
              <strong>{participants}</strong> participant{participants > 1 ? "s" : ""}
            </span>
          </div>
          <AnimatePresence mode="wait">
            {editing ? (
              <motion.button
                key="vote"
                type="button"
                onClick={submitVote}
                disabled={submitting}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                data-testid="submit-vote-button"
                className={`flex w-full items-center justify-center gap-2 rounded-xl px-6 py-4 text-base font-semibold transition-all duration-200 ${
                  !submitting
                    ? "bg-[#171512] text-[#FAF9F5] hover:bg-[#3C6B4E]"
                    : "cursor-not-allowed bg-[#E4E1D8] text-[#9A958A]"
                }`}
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Envoi…
                  </>
                ) : hasVoted ? (
                  "Enregistrer mes modifications"
                ) : (
                  `Je m'engage à jeûner`
                )}
              </motion.button>
            ) : (
              <motion.div
                key="voted"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center justify-between gap-4"
                data-testid="results-footer"
              >
                <div className="flex items-center gap-2 text-sm font-medium text-[#171512]">
                  <Users className="h-4 w-4 text-[#8A8577]" />
                  <span data-testid="participants-count">
                    {participants} participant{participants > 1 ? "s" : ""}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  data-testid="edit-vote-button"
                  className="flex items-center gap-2 rounded-full border border-[#E4E1D8] bg-white px-4 py-2 text-sm font-semibold text-[#171512] transition-colors hover:border-[#3C6B4E] hover:text-[#3C6B4E]"
                >
                  <Pencil className="h-3.5 w-3.5" />
                  Modifier mon vote
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Popup de félicitations */}
      <AnimatePresence>
        {celebrate && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="celebration-overlay"
            onClick={() => setCelebrate(false)}
          >
            <div className="absolute inset-0 bg-[#171512]/40 backdrop-blur-sm" />
            <motion.div
              className="relative w-full max-w-sm rounded-2xl border border-[#E4E1D8] bg-[#FAF9F5] p-8 text-center shadow-xl"
              initial={{ scale: 0.9, y: 12, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: "spring", stiffness: 320, damping: 24 }}
              data-testid="celebration-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setCelebrate(false)}
                data-testid="celebration-close"
                className="absolute right-4 top-4 text-[#8A8577] transition-colors hover:text-[#171512]"
              >
                <X className="h-5 w-5" />
              </button>
              <motion.div
                className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#DBE8DC]"
                initial={{ scale: 0, rotate: -20 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.1, type: "spring", stiffness: 260 }}
              >
                <PartyPopper className="h-8 w-8 text-[#3C6B4E]" />
              </motion.div>
              <h2 className="mt-5 font-serif text-2xl font-semibold">
                Merci pour votre engagement
              </h2>
              <p className="mt-2 text-sm text-[#6B675C]">
                Votre choix a bien été enregistré. Si vous le pouvez,
                diffusez cette initiative autour de vous &nbsp;!
              </p>
              <button
                type="button"
                onClick={() => setCelebrate(false)}
                data-testid="celebration-ok"
                className="mt-6 w-full rounded-xl bg-[#171512] px-6 py-3 text-sm font-semibold text-[#FAF9F5] transition-colors hover:bg-[#3C6B4E]"
              >
                Voir les résultats
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function App() {
  return <Poll />;
}

export default App;


